// useReducedMotion — the canonical prefers-reduced-motion primitive.
//
// The `(prefers-reduced-motion: reduce)` matchMedia pattern is currently rolled
// by hand in ~10 places (FirstRunIntro, SegmentFillPanel, useSegmentFillAnimation,
// tutorialHooks, SegmentEnvironmentLayer, ShareQRSection, RailScroller,
// CreatorInfluence, wheelContentSearch, renderer-v2/engine …). This is the single
// shared version new code should use; the existing copies can migrate here in a
// separate cleanup (they are intentionally left untouched by this unit).
//
// Exposes BOTH shapes the idiom needs:
//   · prefersReducedMotion()  — imperative read, for use OUTSIDE render (e.g. an
//                               IntersectionObserver callback that needs the
//                               CURRENT value, not a render-time snapshot).
//   · useReducedMotion()      — reactive hook that re-renders on OS-setting change.

import { useEffect, useState } from "react";

const QUERY = "(prefers-reduced-motion: reduce)";

export function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(QUERY).matches
  );
}

export default function useReducedMotion() {
  const [reduced, setReduced] = useState(prefersReducedMotion);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(QUERY);
    const onChange = () => setReduced(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange); // Safari < 14
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  return reduced;
}
